import React, { memo, useCallback } from 'react';
import { keyframes, styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { EMOJI_ICONS, EMOJI_WIDTH } from 'features/room-reactions/constants';
import { Chip } from 'features/streaming/components/stream-display/StreamBadge';
import { FormattedReactionItem } from 'features/room-reactions/utils/ReactionsDisplayManager';

export const ANIMATION_DURATION_SECONDS = 4;

const flyUpKeyframes = keyframes`
  /* noinspection CssUnresolvedCustomProperty */
  0% {
    transform: translateY(var(--fly-from-y, 0));
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  65% {
    opacity: 1;
  }

  70% {
    opacity: 0.7;
  }

  /* noinspection CssUnresolvedCustomProperty */
  100% {
    transform: translateY(var(--fly-to-y, 0));
    opacity: 0;
  }
`;

const FloaterPositioner = styled('div')({
  position: 'absolute',
  bottom: 0,
  pointerEvents: 'none',
  zIndex: 500,
  transform: 'translateX(-50%)',
});

const Flyer = styled('div')({
  animation: `${flyUpKeyframes} var(--fly-duration, ${ANIMATION_DURATION_SECONDS}s) ease-out both`,
  willChange: 'transform, opacity',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
});

const EmojiIcon = styled('img')({
  width: `${EMOJI_WIDTH}px`,
  height: `${EMOJI_WIDTH}px`,
  display: 'block',
});

const SenderName = styled(Chip)({
  maxWidth: '100%',
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export type ReactionFloaterProps = {
  item: FormattedReactionItem;
  onAnimationEnd: (id: string) => void;
};

const ReactionFloater = ({ item, onAnimationEnd }: ReactionFloaterProps) => {
  const { t } = useTranslation();

  const label = item.local ? t('common:you') : item.senderName;

  const handleAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onAnimationEnd(item.id);
      }
    },
    [item.id, onAnimationEnd]
  );

  return (
    <FloaterPositioner
      data-testid="reactions-floater"
      style={{ left: `${item.left}px`, maxWidth: `${item.chipMaxWidth}px` }}
    >
      <Flyer
        data-testid="reactions-flyer"
        style={item.delayMs > 0 ? { animationDelay: `${item.delayMs}ms` } : undefined}
        onAnimationEnd={handleAnimationEnd}
      >
        <EmojiIcon src={EMOJI_ICONS[item.reaction]} alt={item.reaction} />
        {label && <SenderName label={label} title={label} />}
      </Flyer>
    </FloaterPositioner>
  );
};

export default memo(ReactionFloater);
